/**
 * Which businesses are "mine" — the rule behind `/my-businesses` (mybiz).
 *
 * `get_my_businesses()` answers "every org this login OWNS", and ownership is
 * whoever pressed Create. An office that sets up a client's books from its own
 * login is recorded as that client's owner, so the founder's office login saw
 * MAHIHOOR, VETINS, TULSIMANPOWER and Synexus as "Every business you own", with a
 * group-CFO report across unrelated clients and a 20%-off pitch for the next
 * one (founder, 26 Sep 2026: "i am in ANATORQ … why is it showing other
 * businesses"). A retired twin ("ZZ RETIRED …") showed too.
 *
 * So a business is mine when I own it, it is not a client on my office's list,
 * and it has not been retired. The link to the page also steps aside inside a
 * client's books: it sat under ANATORQ's Dashboard and read as ANATORQ's.
 *
 * Pure, so `myBusinesses.test.ts` pins every branch.
 */
import { isVisitingClientBooks, type OfficeMembership } from './officeIdentity';

/** Just enough of an `Org` to decide. Matches `types/db.ts`'s `Org`. */
export interface BusinessMembership extends OfficeMembership {
    /** On the office's client list (`practice_clients`), set by `useAuth`. */
    is_practice_client?: boolean | null;
}

/**
 * An org is never deleted, only renamed "ZZ RETIRED …" with its books kept
 * (see `scripts/cleanup-jk-hope-duplicate-org.sql`). The name is the marker.
 */
export function isRetiredOrgName(name: string | null | undefined): boolean {
    return /^zz retired/i.test((name ?? '').trim());
}

/** Do I own this one as MY business, rather than run it for a client? */
export function isOwnBusiness(m: BusinessMembership): boolean {
    return m.role === 'owner' && !m.is_practice_client && !isRetiredOrgName(m.name);
}

/**
 * Does the My Businesses link belong in this org's nav?
 *
 * Not inside a client's books: as a visiting professional, or in a client
 * company the office happens to own because it created it. The page itself
 * stays reachable by URL; only the door that looked like the client's steps out.
 */
export function showsMyBusinessesLink(
    currentOrgId: string | null | undefined,
    orgs: readonly BusinessMembership[],
): boolean {
    if (!currentOrgId) return true;
    if (isVisitingClientBooks(currentOrgId, orgs)) return false;
    const here = orgs.find(o => o.id === currentOrgId);
    return !(here?.is_practice_client || isRetiredOrgName(here?.name));
}

/**
 * The client's name while I stand in a client's books, else null: as a
 * visiting professional, or in a client company the office owns because it
 * created it (waoffice).
 *
 * The Karr Phone used {@link isVisitingClientBooks} alone, which calls an owned
 * org mine. The office login owns MAHIHOOR, so inside MAHIHOOR's books the
 * phone opened MAHIHOOR's own WhatsApp, empty and unconnected, and offered to
 * connect it, while the same login in the office had every chat (founder,
 * 7 Oct 2026: "on her id ... it says connect").
 */
export function clientBooksName(
    currentOrgId: string | null | undefined,
    orgs: readonly BusinessMembership[],
): string | null {
    if (!currentOrgId) return null;
    const here = orgs.find(o => o.id === currentOrgId);
    if (isVisitingClientBooks(currentOrgId, orgs) || here?.is_practice_client) {
        return here?.name ?? 'this client';
    }
    return null;
}

export interface BusinessRow {
    org_id: string;
    name: string;
    cash: number;
    receivables: number;
    payables: number;
    profit_mtd: number;
    risk_exposure: number;
}
export interface BusinessSummary {
    count: number; cash: number; receivables: number; payables: number; profit_mtd: number; risk_exposure: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/**
 * The RPC's roster, narrowed to MY businesses, with "You owe" the right way up.
 *
 * ⚠ SIGN: `payables` is read off `running_balances`, which stores
 * `debit − credit`, so a supplier owed ₹112 arrives as −112 and the card read
 * "You owe ₹-112.00". Flipped here, at the one boundary, so the cards, the
 * totals, the export and the group-CFO snapshot all agree. If the RPC is ever
 * changed to return it positive, remove this flip in the same PR.
 *
 * A row whose membership is not in `orgs` (the org list loads separately) is
 * kept unless its name says retired: the RPC already checked ownership, and
 * guessing "client" would blank a real owner's page.
 */
export function ownBusinessesOnly<T extends BusinessRow>(
    businesses: readonly T[],
    orgs: readonly BusinessMembership[],
): { businesses: T[]; summary: BusinessSummary } {
    const byId = new Map(orgs.map(o => [o.id, o]));
    const mine = businesses
        .filter((b) => {
            const m = byId.get(b.org_id);
            return m ? isOwnBusiness({ ...m, role: 'owner', name: m.name ?? b.name }) : !isRetiredOrgName(b.name);
        })
        .map(b => ({ ...b, payables: -b.payables || 0 }));
    const sum = (k: keyof BusinessRow) => round2(mine.reduce((s, b) => s + Number(b[k] || 0), 0));
    return {
        businesses: mine,
        summary: {
            count: mine.length,
            cash: sum('cash'),
            receivables: sum('receivables'),
            payables: sum('payables'),
            profit_mtd: sum('profit_mtd'),
            risk_exposure: sum('risk_exposure'),
        },
    };
}
